import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Modal, Btn } from "@/components/common";
import BookingForm from "@/components/BookingForm";
import { CalendarGrid } from "@/components/CalendarGrid";
import { MONTHS, STATUS_LEGEND, expandBars, addDaysStr, todayStr, firstOfMonthStr, diffDays } from "@/lib/calendarUtils";
import { exportCalendarMonthPdf } from "@/lib/calendarPdf";
import { ChevronLeft, ChevronRight, Plus, Printer } from "lucide-react";

export default function Calendario() {
  const { isAdmin, isStaff, user } = useAuth();
  const navigate = useNavigate();
  const [start, setStart] = useState(() => firstOfMonthStr());
  const [numDays, setNumDays] = useState(31);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [origini, setOrigini] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [printMonths, setPrintMonths] = useState(1);
  const [printStruct, setPrintStruct] = useState("");

  const load = () => {
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/bookings/calendar").then((r) => setBookings(r.data));
    api.get("/settings").then((r) => { setOrigini(r.data.origini || []); setAgencies(r.data.agencies_config || []); });
  };
  useEffect(() => { load(); }, []);

  const curYear = Number(start.slice(0, 4));
  const curMonth = Number(start.slice(5, 7)) - 1;
  const goMonth = (y, m) => setStart(`${y}-${String(m + 1).padStart(2, "0")}-01`);
  const _now = new Date();
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(_now.getFullYear(), _now.getMonth() + i, 1);
    return { y: d.getFullYear(), m: d.getMonth(), key: `${d.getFullYear()}-${d.getMonth()}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` };
  });
  const curKey = `${curYear}-${curMonth}`;
  const MIN_DATE = "2025-12-01";
  const MAX_RANGE = 92; // massimo 3 mesi per intervallo
  const rangeEnd = addDaysStr(start, numDays - 1);
  const applyRange = (from, to) => {
    if (!from || !to) return;
    if (from < MIN_DATE) from = MIN_DATE;
    let d = diffDays(from, to) + 1;
    if (d < 1) d = 1;
    if (d > MAX_RANGE) { d = MAX_RANGE; toast.info("Intervallo massimo 3 mesi: mostrati i primi 92 giorni. Usa la barra o \u2039 15 GG \u203a per scorrere."); }
    setStart(from);
    setNumDays(d);
  };
  const propName = (id) => properties.find((p) => p.id === id)?.name || "—";
  const structOptions = useMemo(() => {
    const seen = new Map();
    properties.forEach((p) => { if (p.structure_id && !seen.has(p.structure_id)) seen.set(p.structure_id, p.structure_name || "Struttura"); });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [properties]);

  const days = useMemo(
    () => Array.from({ length: numDays }, (_, i) => addDaysStr(start, i)),
    [start, numDays]
  );
  const todayIso = todayStr();
  const bars = useMemo(() => expandBars(bookings), [bookings]);

  const [pendingMove, setPendingMove] = useState(null);
  const [moveAvail, setMoveAvail] = useState(null);

  const onReschedule = (booking, toRoom, checkin, checkout) => {
    setMoveAvail(null);
    setPendingMove({ booking, toRoom, checkin, checkout });
  };

  useEffect(() => {
    if (!pendingMove) return;
    let active = true;
    api.get("/bookings/availability", { params: { property_id: pendingMove.toRoom, checkin: pendingMove.checkin, checkout: pendingMove.checkout, exclude: pendingMove.booking.id } })
      .then((r) => { if (active) setMoveAvail(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [pendingMove]);

  const confirmMove = async () => {
    const { booking, toRoom, checkin, checkout } = pendingMove;
    setPendingMove(null);
    try {
      await api.patch(`/bookings/${booking.id}/reschedule`, { to_property_id: toRoom, checkin, checkout });
      toast.success("Prenotazione spostata");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Spostamento non riuscito");
      load();
    }
  };

  const totals = useMemo(() => {
    return days.map((day) => {
      let arrivi = 0, partenze = 0, presenze = 0;
      bookings.forEach((b) => {
        const pax = (b.adults || 0) + (b.children || 0);
        if (b.checkin === day) arrivi += 1;
        if (b.checkout === day) partenze += 1;
        if (b.checkin <= day && day < b.checkout) presenze += pax;
      });
      return { arrivi, partenze, presenze };
    });
  }, [days, bookings]);

  // Presenze in-house divise per guesthouse (struttura), conteggiate sul segmento/camera occupata
  const presenceRows = useMemo(() => {
    const roomStruct = {};
    const structOrder = [];
    const structName = {};
    properties.forEach((p) => {
      roomStruct[p.id] = p.structure_id;
      if (!structName[p.structure_id]) {
        structName[p.structure_id] = p.structure_name || "Altro";
        structOrder.push(p.structure_id);
      }
    });
    return structOrder.map((sid) => {
      const values = days.map((day) => {
        let n = 0;
        bars.forEach((x) => {
          if (roomStruct[x.property_id] !== sid) return;
          if (x.checkin <= day && day < x.checkout) {
            n += (x.booking.adults || 0) + (x.booking.children || 0);
          }
        });
        return n;
      });
      return { structure_id: sid, label: structName[sid], values };
    });
  }, [days, bars, properties]);

  return (
    <div>
      <PageHeader title="Calendario" subtitle="Planning camere · prenotazioni">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="cal-month-year"
            value={monthOptions.some((o) => o.key === curKey) ? curKey : ""}
            onChange={(e) => { const o = monthOptions.find((x) => x.key === e.target.value); if (o) goMonth(o.y, o.m); }}
            className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white outline-none focus:ring-2 focus:ring-sky-500 font-medium"
          >
            <option value="">Seleziona mese…</option>
            {monthOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <button data-testid="cal-today" onClick={() => { setStart(firstOfMonthStr()); setNumDays(31); }} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-sky-600 bg-sky-50 hover:bg-sky-100 text-sm font-semibold text-sky-700">
            Oggi
          </button>
          <button data-testid="cal-prev" onClick={() => setStart(addDaysStr(start, -15))} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700">
            <ChevronLeft size={16} /> 15 GG
          </button>
          <button data-testid="cal-next" onClick={() => setStart(addDaysStr(start, 15))} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700">
            15 GG <ChevronRight size={16} />
          </button>
          <select value={numDays} onChange={(e) => setNumDays(Number(e.target.value))} data-testid="cal-range" className="px-2 py-2 rounded-md border border-slate-300 text-sm bg-white">
            <option value={15}>15 gg</option>
            <option value={31}>31 gg</option>
            <option value={45}>45 gg</option>
          </select>
          <div className="hidden md:flex items-center gap-1 px-2 py-1.5 rounded-md border border-slate-300 bg-white" title="Scegli un intervallo (max 3 mesi, indietro fino a Dic 2025)">
            <span className="text-xs text-slate-500">Da</span>
            <input data-testid="cal-from" type="date" min={MIN_DATE} value={start}
              onChange={(e) => applyRange(e.target.value, rangeEnd)}
              className="text-sm outline-none bg-transparent w-[125px]" />
            <span className="text-xs text-slate-500">a</span>
            <input data-testid="cal-to" type="date" min={start}
              value={rangeEnd} onChange={(e) => applyRange(start, e.target.value)}
              className="text-sm outline-none bg-transparent w-[125px]" />
          </div>
          <select data-testid="cal-print-struct" value={printStruct} onChange={(e) => setPrintStruct(e.target.value)} className="hidden md:block px-2 py-2 rounded-md border border-slate-300 text-sm bg-white" title="Quale guesthouse stampare">
            <option value="">Tutte le strutture</option>
            {structOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select data-testid="cal-print-months" value={printMonths} onChange={(e) => setPrintMonths(Number(e.target.value))} className="hidden md:block px-2 py-2 rounded-md border border-slate-300 text-sm bg-white" title="Quanti mesi consecutivi stampare">
            <option value={1}>1 mese</option>
            <option value={3}>3 mesi</option>
            <option value={6}>6 mesi (stagione)</option>
            <option value={12}>12 mesi</option>
          </select>
          <button data-testid="cal-print-pdf" onClick={() => exportCalendarMonthPdf({ year: curYear, month: curMonth, properties, bars, months: printMonths, structureId: printStruct, structureName: structOptions.find((s) => s.id === printStruct)?.name || "" })} className="hidden md:inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700" title="Stampa il calendario in PDF (orizzontale), dal mese selezionato">
            <Printer size={16} /> Stampa PDF
          </button>
          <Btn data-testid="cal-new-booking" onClick={() => setShowNew(true)} className={isStaff ? "hidden" : ""}>
            <Plus size={16} /> Nuova prenotazione
          </Btn>
        </div>
      </PageHeader>

      <CalendarGrid
        start={start}
        numDays={numDays}
        days={days}
        properties={properties}
        bars={bars}
        totals={totals}
        presenceRows={presenceRows}
        todayIso={todayIso}
        onSelect={setSelected}
        onReschedule={onReschedule}
        isAdmin={isAdmin}
      />

      {/* legend */}
      <div className="flex flex-wrap gap-3 mt-4">
        {STATUS_LEGEND.map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className={`w-3 h-3 rounded ${c}`} /> {k}
          </div>
        ))}
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Dettaglio prenotazione">
        {selected && (
          <div className="space-y-2 text-sm" data-testid="cal-detail">
            <Row label="Ospite" value={`${selected.guest_name} ${selected.guest_surname}`} />
            <Row label="Stato" value={selected.status === "opzionata" ? "Opzionata" : selected.status === "cancellata" ? "Cancellata" : "Confermata"} />
            <Row label="Agenzia / Origine" value={selected.origine} />
            <Row label="Check-in" value={selected.checkin} />
            <Row label="Check-out" value={selected.checkout} />
            <Row label="Persone" value={`${selected.adults} adulti · ${selected.children} bambini`} />
            {(selected.own || isAdmin) && (
              <div className="pt-3">
                <Btn className="w-full" data-testid="cal-edit-money" onClick={() => { const id = selected.id; setSelected(null); navigate(`/money?edit=${id}`); }}>Modifica in Prenotazioni</Btn>
              </div>
            )}
            {(selected.own || isAdmin) && (
              <MoveControl selected={selected} properties={properties} onMoved={() => { setSelected(null); load(); }} />
            )}
            {!selected.own && !isAdmin && (
              <p className="text-xs text-slate-400 pt-2">
                I dettagli economici sono visibili solo all'amministratore o all'agenzia proprietaria.
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="Nuova prenotazione" wide>
        {showNew && (
          <BookingForm
            properties={properties}
            origini={origini}
            agencies={agencies}
            isAdmin={isAdmin}
            user={user}
            onSaved={() => { setShowNew(false); load(); }}
            onCancel={() => setShowNew(false)}
          />
        )}
      </Modal>

      <Modal open={!!pendingMove} onClose={() => setPendingMove(null)} title="Conferma spostamento">
        {pendingMove && (
          <div className="space-y-3 text-sm" data-testid="move-confirm-modal">
            <p>Vuoi spostare <b>{pendingMove.booking.guest_name} {pendingMove.booking.guest_surname}</b>?</p>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1">
              <div className="flex items-center gap-2">Camera: <b>{propName(pendingMove.booking.property_id)}</b> → <b className="text-sky-700">{propName(pendingMove.toRoom)}</b></div>
              <div>Date: <b>{pendingMove.checkin}</b> → <b>{pendingMove.checkout}</b></div>
            </div>
            {moveAvail && (
              <div data-testid="move-confirm-avail" className={`text-xs px-2 py-1.5 rounded ${moveAvail.available ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                {moveAvail.available ? "Camera libera in queste date." : "Attenzione: camera già occupata in queste date. Spostamento non consentito."}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Btn variant="secondary" data-testid="move-confirm-cancel" onClick={() => setPendingMove(null)}>Annulla</Btn>
              <Btn data-testid="move-confirm-ok" disabled={moveAvail && !moveAvail.available} onClick={confirmMove}>OK, sposta</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  );
}

function MoveControl({ selected, properties, onMoved }) {
  const [target, setTarget] = useState("");
  const [avail, setAvail] = useState(null);
  const [moving, setMoving] = useState(false);
  const from = selected.seg_property_id || selected.property_id;
  const ci = selected.seg_checkin || selected.checkin;
  const co = selected.seg_checkout || selected.checkout;
  useEffect(() => {
    if (!target) { setAvail(null); return; }
    let active = true;
    api.get("/bookings/availability", { params: { property_id: target, checkin: ci, checkout: co, exclude: selected.id } })
      .then((r) => { if (active) setAvail(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [target, ci, co, selected.id]);
  const fromName = properties.find((p) => p.id === from)?.name || "—";
  const targetName = properties.find((p) => p.id === target)?.name || "";
  const groups = Object.entries(
    properties.filter((p) => p.id !== from).reduce((acc, p) => {
      (acc[p.structure_name || "Camere"] = acc[p.structure_name || "Camere"] || []).push(p);
      return acc;
    }, {})
  );
  const doMove = async () => {
    if (!target || moving) return;
    setMoving(true);
    try {
      await api.patch(`/bookings/${selected.id}/move`, { from_property_id: from, to_property_id: target });
      toast.success("Prenotazione spostata");
      onMoved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Spostamento non riuscito");
      setMoving(false);
    }
  };
  return (
    <div className="pt-3 mt-2 border-t border-slate-200" data-testid="move-control">
      <div className="text-xs font-semibold text-slate-600 mb-1">Sposta camera</div>
      <div className="text-[11px] text-slate-400 mb-2">Da <b>{fromName}</b> · {ci} → {co}</div>
      <select data-testid="move-target" value={target} onChange={(e) => setTarget(e.target.value)} className="w-full px-2 py-2 rounded-md border border-slate-300 text-sm bg-white outline-none focus:ring-2 focus:ring-sky-500">
        <option value="">Sposta in un'altra camera…</option>
        {groups.map(([grp, rooms]) => (
          <optgroup key={grp} label={grp}>
            {rooms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </optgroup>
        ))}
      </select>
      {target && avail && (
        <div data-testid="move-alert" className={`mt-2 text-xs px-2 py-1.5 rounded ${avail.available ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          Sposti <b>{selected.guest_name} {selected.guest_surname}</b> dal <b>{ci}</b> al <b>{co}</b> → <b>{targetName}</b>. {avail.available ? "Camera libera in queste date." : "Attenzione: camera già occupata in queste date."}
        </div>
      )}
      <Btn className="w-full mt-2" data-testid="move-confirm" disabled={!target || moving || (avail && !avail.available)} onClick={doMove}>
        {moving ? "Spostamento…" : "Sposta qui"}
      </Btn>
    </div>
  );
}
